import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { productsAPI, marketplaceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Payment.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// TWINT temporairement désactivé (passer à true pour réactiver)
const TWINT_ENABLED = false;

const BuyProductForm = ({ product, onSuccess }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [shippingMethod, setShippingMethod] = useState(
    product.shipping_available ? 'shipping' : product.local_pickup ? 'pickup' : 'shipping'
  );
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    street: '',
    city: '',
    postalCode: '',
    country: 'Suisse'
  });
  const [orderTotal, setOrderTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: options, 2: paiement
  const [buyerMessage, setBuyerMessage] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    calculateTotal();
  }, [shippingMethod]);

  const calculateTotal = async () => {
    try {
      const response = await marketplaceAPI.calculateOrderTotal(product.id, shippingMethod);
      setOrderTotal(response.data);
    } catch (err) {
      setError(t('buy.priceCalcError'));
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({ ...prev, [name]: value }));
  };

  const validateAddress = () => {
    if (shippingMethod === 'pickup') return true;
    const { name, street, city, postalCode } = shippingAddress;
    return name && street && city && postalCode;
  };

  const handleProceedToPayment = () => {
    if (!validateAddress()) {
      setError(t('buy.fillAddress'));
      return;
    }
    setError('');
    setStep(2);
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Créer l'intention de paiement
      const intentResponse = await marketplaceAPI.createPurchaseIntent(
        product.id,
        shippingMethod,
        shippingMethod === 'shipping' ? shippingAddress : null,
        buyerMessage || null,
        paymentMethod
      );

      const { clientSecret, orderId } = intentResponse.data;

      if (paymentMethod === 'twint') {
        // Paiement Twint - redirection
        const { error: stripeError } = await stripe.confirmTwintPayment(clientSecret, {
          payment_method: {
            billing_details: {
              name: shippingAddress.name || user?.username || '',
              email: user?.email || '',
            },
          },
          return_url: `${window.location.origin}/payment-success?type=order`,
        });
        if (stripeError) {
          setError(stripeError.message);
          setLoading(false);
        }
        return;
      }

      // Paiement par carte
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        await marketplaceAPI.confirmPurchase(paymentIntent.id);
        onSuccess(orderId);
      }

    } catch (err) {
      setError(err.response?.data?.message || t('buy.paymentError'));
    } finally {
      setLoading(false);
    }
  };

  const imageUrl = product.images && product.images.length > 0
    ? `${API_URL.replace('/api', '')}/uploads/${product.images[0]}`
    : 'https://via.placeholder.com/150x150?text=Pas+d\'image';

  return (
    <div className="buy-product-form">
      {/* Résumé du produit */}
      <div className="product-summary">
        <img src={imageUrl} alt={product.title} className="product-thumb" />
        <div className="product-info">
          <h3>{product.title}</h3>
          <p className="product-price">{parseFloat(product.price).toFixed(2)} CHF</p>
          <p className="seller-info">{t('product.sellerLabel')} {product.seller_name}</p>
        </div>
      </div>

      {step === 1 && (
        <>
          {/* Options de livraison */}
          <div className="shipping-options">
            <h3>{t('buy.shippingMethod')}</h3>

            {product.shipping_available ? (
              <label className={`shipping-option ${shippingMethod === 'shipping' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="shipping"
                  value="shipping"
                  checked={shippingMethod === 'shipping'}
                  onChange={(e) => setShippingMethod(e.target.value)}
                />
                <div className="option-content">
                  <span className="option-icon">&#128230;</span>
                  <div>
                    <strong>{t('buy.delivery')}</strong>
                    <p>{t('buy.deliveryCost', { price: product.shipping_price || 7 })}</p>
                  </div>
                </div>
              </label>
            ) : null}

            {product.local_pickup ? (
              <label className={`shipping-option ${shippingMethod === 'pickup' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="shipping"
                  value="pickup"
                  checked={shippingMethod === 'pickup'}
                  onChange={(e) => setShippingMethod(e.target.value)}
                />
                <div className="option-content">
                  <span className="option-icon">&#128205;</span>
                  <div>
                    <strong>{t('buy.pickup')}</strong>
                    <p>{t('common.free')}</p>
                  </div>
                </div>
              </label>
            ) : null}

            {/* Adresse de retrait */}
            {shippingMethod === 'pickup' && product.local_pickup && (
              <div className="pickup-address-info">
                <h4>{t('buy.pickupAddress')}</h4>
                <p className="pickup-address">
                  {product.street && product.street.trim() && (
                    <>{product.street.trim()}<br /></>
                  )}
                  {(product.postal_code || product.city) && (
                    <>
                      {product.postal_code && String(product.postal_code).trim()}{' '}
                      {product.city && product.city.trim()}
                    </>
                  )}
                </p>
                <p className="pickup-note">
                  {t('buy.pickupNote')}
                </p>
              </div>
            )}
          </div>

          {/* Adresse de livraison */}
          {shippingMethod === 'shipping' && (
            <div className="shipping-address">
              <h3>{t('buy.deliveryAddress')}</h3>
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder={t('buy.fullName')}
                  value={shippingAddress.name}
                  onChange={handleAddressChange}
                  required
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  name="street"
                  placeholder={t('buy.addressLabel')}
                  value={shippingAddress.street}
                  onChange={handleAddressChange}
                  required
                />
              </div>
              <div className="form-row">
                <input
                  type="text"
                  name="postalCode"
                  placeholder={t('buy.postalCode')}
                  value={shippingAddress.postalCode}
                  onChange={handleAddressChange}
                  required
                />
                <input
                  type="text"
                  name="city"
                  placeholder={t('buy.city')}
                  value={shippingAddress.city}
                  onChange={handleAddressChange}
                  required
                />
              </div>
            </div>
          )}

          {/* Message pour le vendeur */}
          <div className="buyer-message-section">
            <h3>{t('buy.messageToSeller')}</h3>
            <textarea
              value={buyerMessage}
              onChange={(e) => setBuyerMessage(e.target.value)}
              placeholder={t('buy.messagePlaceholder')}
              rows="3"
              maxLength="500"
              className="buyer-message-input"
            />
          </div>

          {/* Récapitulatif des prix */}
          {orderTotal && (
            <div className="price-breakdown">
              <h3>{t('buy.summary')}</h3>
              <div className="price-line">
                <span>{t('buy.item')}</span>
                <span>{orderTotal.productPrice.toFixed(2)} CHF</span>
              </div>
              {shippingMethod === 'shipping' && (
                <div className="price-line">
                  <span>{t('buy.shippingFee')}</span>
                  <span>{orderTotal.shippingPrice.toFixed(2)} CHF</span>
                </div>
              )}
              {orderTotal.buyerProtection > 0 && (
                <div className="price-line protection">
                  <span>{t('buy.protectionFee')}</span>
                  <span>{orderTotal.buyerProtection.toFixed(2)} CHF</span>
                </div>
              )}
              {orderTotal.serviceFee > 0 && (
                <div className="price-line">
                  <span>{orderTotal.serviceFeeRate === 0.04 ? t('vehicle.serviceFee') : t('buy.serviceFee')}</span>
                  <span>{orderTotal.serviceFee.toFixed(2)} CHF</span>
                </div>
              )}
              <div className="price-line total">
                <span>{t('buy.total')}</span>
                <span>{orderTotal.total.toFixed(2)} CHF</span>
              </div>
            </div>
          )}

          <div className="protection-info">
            <span className="info-icon">&#128274;</span>
            <p>
              <strong>{t('buy.protectionFee')} :</strong> {t('buy.protectionInfo')}
            </p>
          </div>

          <a
            href="https://www.post.ch/fr/expedier-des-colis/colis-suisse"
            target="_blank"
            rel="noopener noreferrer"
            className="post-help-link"
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/ae/Die_Post_Logo.svg/200px-Die_Post_Logo.svg.png"
              alt="La Poste Suisse"
              className="post-logo"
            />
            <span>{t('buy.shippingHelp')}</span>
          </a>

          {error && <div className="error-message">{error}</div>}

          <button
            className="btn-primary btn-large"
            onClick={handleProceedToPayment}
            disabled={!orderTotal}
          >
            {t('buy.continueToPayment')}
          </button>
        </>
      )}

      {step === 2 && (
        <form onSubmit={handlePayment}>
          <h3>{t('buy.securePayment')}</h3>

          <div className="payment-summary">
            <p>{t('buy.totalToPay', { amount: orderTotal?.total.toFixed(2) })}</p>
          </div>

          {/* Choix méthode de paiement */}
          <div className="payment-method-choice">
            <label className={`payment-method-option ${paymentMethod === 'card' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="paymentMethod"
                value="card"
                checked={paymentMethod === 'card'}
                onChange={() => setPaymentMethod('card')}
              />
              <span className="payment-method-icon">&#128179;</span>
              <span>{t('buy.creditCard')}</span>
            </label>
            {TWINT_ENABLED && (
              <label className={`payment-method-option ${paymentMethod === 'twint' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value="twint"
                  checked={paymentMethod === 'twint'}
                  onChange={() => setPaymentMethod('twint')}
                />
                <span className="payment-method-icon">&#128241;</span>
                <span translate="no">TWINT</span>
              </label>
            )}
          </div>

          {paymentMethod === 'card' && (
            <div className="card-element-container">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': { color: '#aab7c4' },
                    },
                    invalid: { color: '#9e2146' },
                  },
                }}
              />
            </div>
          )}

          {paymentMethod === 'twint' && (
            <div className="twint-info">
              <p>{t('buy.twintRedirect')}</p>
              <p>{t('buy.twintInstall')}</p>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="button-group">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setStep(1)}
              disabled={loading}
            >
              {t('buy.back')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!stripe || loading}
            >
              {loading ? t('buy.paying') : <span translate="no">{paymentMethod === 'twint' ? t('buy.payButtonTwint', { amount: orderTotal?.total.toFixed(2) }) : t('buy.payButton', { amount: orderTotal?.total.toFixed(2) })}</span>}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const BuyProduct = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await productsAPI.getById(id);
      setProduct(response.data);
    } catch (err) {
      setError(t('product.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = (orderId) => {
    setOrderId(orderId);
    setSuccess(true);
  };

  if (loading) {
    return (
      <div className="payment-container">
        <div className="payment-card">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-container">
        <div className="payment-card">
          <div className="error-message">{error}</div>
          <button className="btn-primary" onClick={() => navigate('/')}>
            {t('common.backToHome')}
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="payment-container">
        <div className="payment-card success-card">
          <div className="success-icon">&#10003;</div>
          <h2>{t('buy.orderConfirmed')}</h2>
          <p>
            {t('buy.paymentAccepted')}
          </p>

          <div className="next-steps">
            <h3>{t('buy.nextSteps')}</h3>
            <ol>
              <li>{t('buy.step1')}</li>
              <li>{t('buy.step2')}</li>
              <li>{t('buy.step3')}</li>
            </ol>
          </div>

          <div className="button-group">
            <button className="btn-primary" onClick={() => navigate('/my-purchases')}>
              {t('buy.viewPurchases')}
            </button>
            <button className="btn-secondary" onClick={() => navigate('/')}>
              {t('buy.continueShopping')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-container">
      <div className="payment-card">
        <h2>{t('buy.title')}</h2>
        <Elements stripe={stripePromise}>
          <BuyProductForm product={product} onSuccess={handleSuccess} />
        </Elements>
      </div>
    </div>
  );
};

export default BuyProduct;
